import React, { useEffect, useState } from "react";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack,
} from "@mui/material";
import StandardDialog from "./StandardDialog";
import useApi from "../hooks/useApi";
import { toast } from "react-toastify";
import { getContentLabel, handleApiError } from "../utils/utils";

const FlagDialog = ({ open, onClose, contentType, contentId }) => {
  const isBook = contentType === "book";
  const [option, setOption] = useState(isBook ? "cover" : "import");
  const [message, setMessage] = useState("");
  const { flagContent } = useApi();

  // Reset form whenever dialog opens, defaulting to the first visible option for this content type
  useEffect(() => {
    if (open) {
      setOption(isBook ? "cover" : "import");
      setMessage("");
    }
  }, [open, isBook]);

  const imageLabel =
    contentType === "book"
      ? "Book cover"
      : contentType === "movie"
        ? "Movie poster"
        : "TV show poster";

  const handleSubmit = async () => {
    if (!message) return;

    try {
      const response = await flagContent({ contentId, contentType, option, message });
      toast.success(response?.message);
      onClose();
    } catch (error) {
      handleApiError(error);
    }
  };

  return (
    <StandardDialog open={open} onClose={onClose} maxWidth="xs">
      <DialogTitle sx={{ pb: 0.5 }}>Something doesn't look right?</DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <FormControl sx={{ mb: 1 }}>
          <RadioGroup value={option} onChange={(e) => setOption(e.target.value)}>
            {isBook ? (
              // Books: cover and metadata are user-controllable
              <>
                <FormControlLabel
                  value="cover"
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2">
                      {imageLabel} is incorrect / poor quality
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="metadata"
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2">{`${getContentLabel({
                      type: contentType,
                    })} metadata is incorrect`}</Typography>
                  }
                />
              </>
            ) : (
              // Movies/TV: poster and metadata come from TMDB and can't be changed directly
              <>
                <FormControlLabel
                  value="import"
                  control={<Radio size="small" />}
                  label={
                    <Stack direction="column" spacing={0}>
                      <Typography variant="body2">
                        {`${getContentLabel({ type: contentType })} incorrectly assigned during import`}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        e.g. your rating / review is tagged to the wrong{" "}
                        {getContentLabel({ type: contentType, plural: false, lowercase: true })}
                      </Typography>
                    </Stack>
                  }
                />
                <FormControlLabel
                  value="other"
                  control={<Radio size="small" />}
                  label={<Typography variant="body2">Other</Typography>}
                />
              </>
            )}
          </RadioGroup>
        </FormControl>
        <Typography variant="body2" gutterBottom>
          Please give as much detail as you can and we'll look into it
        </Typography>
        <TextField
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          fullWidth
          margin="dense"
          autoFocus
          required
          multiline
          rows={3}
          size="small"
          InputProps={{
            sx: { typography: "body2", color: "text.secondary" },
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!message}>
          Submit
        </Button>
      </DialogActions>
    </StandardDialog>
  );
};

export default FlagDialog;
